import { useEffect } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4lang_es_ES from '@amcharts/amcharts4/lang/es_ES';
import { formattingSeries } from '../InstantBasic/helpers';

export default function DualChartAdvanced({
    chartData,
    tz,
    auxHeight,
    zoomBar,
    ky,
    filter,
}) {
    useEffect(() => {
        const chartsContainer = document.getElementById(
            `instantaneos-avanzado${ky ? `${ky}` : ''}`
        );

        const createdCharts = [];
        const charts = Object.groupBy(chartData, ({ groupIndex }) => groupIndex);
        const chartKeys = Object.keys(charts);
        const chartCount = chartKeys.length;
        const baseHeight = 700;
        const heightPerChart = chartCount > 1 ? `${450}px` : `${baseHeight}px`;

        const interaction = am4core.getInteraction();
        const disposers = [];

        const stopNativeEvent = (ev) => {
            const nativeEv = ev?.event;
            if (!nativeEv) return;
            if (typeof nativeEv.preventDefault === 'function') nativeEv.preventDefault();
            if (typeof nativeEv.stopPropagation === 'function') nativeEv.stopPropagation();
            if (typeof nativeEv.stopImmediatePropagation === 'function') nativeEv.stopImmediatePropagation();
        };

        const restoreCursorNextFrame = (chart, behavior, interactionsEnabledSetter) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!chart?.cursor || chart.isDisposed()) return;
                    chart.cursor.behavior = behavior;
                    if (typeof interactionsEnabledSetter === 'boolean') {
                        chart.cursor.interactionsEnabled = interactionsEnabledSetter;
                    } else {
                        chart.cursor.interactionsEnabled = true;
                    }
                });
            });
        };

        for (const chartKey in charts) {
            var formatsUsed = [];
            const chartDataSet = charts[chartKey];
            const chartDivId = `chart-${ky ? ky : ''}-${chartKey}`;

            const chartDiv = document.createElement('div');
            chartDiv.id = chartDivId;
            chartDiv.style.width = '100%';
            chartDiv.style.height = !auxHeight ? heightPerChart : '100%';
            chartDiv.style.marginRight = '50px';
            chartsContainer.appendChild(chartDiv);

            const chart = am4core.create(chartDivId, am4charts.XYChart);
            createdCharts.push(chart);

            const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
            dateAxis.renderer.minGridDistance = 40;
            dateAxis.tooltipDateFormat = 'EEE dd/MM/yyyy HH:mm';
            dateAxis.periodChangeDateFormats.setKey('hour', 'EEE dd HH:mm');
            dateAxis.dateFormats.setKey('day', 'EEE dd MMM');
            dateAxis.periodChangeDateFormats.setKey('day', 'EEE dd');

            chart.fontSize = 11;
            chart.language.locale = am4lang_es_ES;
            chart.dateFormatter.timezone = tz;
            chart.dateFormatter.inputDateFormat = 'yyyy-MM-ddTHH:mm:ssZ';
            if (filter?.selectedInitialDate && filter?.selectedEndDate) {
                const d1 = new Date(filter.selectedInitialDate);
                d1.setHours(0, 0, 0, 0);
                const d2 = new Date(filter.selectedEndDate);
                d2.setHours(23, 59, 0, 0);
                dateAxis.min = d1.getTime();
                dateAxis.max = d2.getTime();
            }
            chart.scrollbarX = new am4charts.XYChartScrollbar();
            chart.scrollbarX.parent = chart.bottomAxesContainer;
            chart.scrollbarX.strokeWidth = 1;
            if (zoomBar) chart.scrollbarX.disabled = true;
            chart.cursor = new am4charts.XYCursor();
            chart.cursor.strokeWidth = 0;
            chart.plotContainer.background = new am4core.Rectangle();
            chart.plotContainer.background.fill = am4core.color('#ffffff');
            chart.plotContainer.background.fillOpacity = 0.001;
            chart.plotContainer.background.strokeOpacity = 0;
            chart.legend = new am4charts.Legend();

            if (!zoomBar) {
                chart.exporting.menu = new am4core.ExportMenu();
                chart.exporting.adapter.add('data', function () {
                    const myData = [];
                    chart.series.each(function (series) {
                        for (var i = 0; i < series.data.length; i++) {
                            series.data[i].name = series?.name;
                            myData.push(series.data[i]);
                        }
                    });
                    return { data: myData };
                });

                chart.exporting.menu.items = [
                    {
                        label: '...',
                        menu: [
                            { type: 'pdf', label: 'PDF' },
                            { type: 'csv', label: 'CSV' },
                            { type: 'xlsx', label: 'XLSX' },
                            { type: 'print', label: 'Imprimir' },
                        ],
                    },
                ];
            }

            chartDataSet.forEach((dataset, indexDataset) => {
                updateChart(chart, dataset, indexDataset);
            });

            if (chart?.logo) chart.logo.disabled = true;

            // ==========================================================
            // Línea horizontal + handle draggable + selector de eje
            // ==========================================================
            const applyRangeStyle = (range) => {
                range.grid.stroke = am4core.color('#000000');
                range.grid.strokeWidth = 2;
                range.grid.strokeOpacity = 1;
                range.grid.above = true;
                range.grid.strokeDasharray = '';
                range.label.background.fill = am4core.color('#c0c0c0');
                range.label.background.fillOpacity = 0.9;
                range.label.padding(2, 6, 2, 6);
                range.label.cornerRadius = 3;
                range.label.fontSize = 15;
                range.label.fill = am4core.color('#000000');
                range.label.inside = true;
                range.label.horizontalCenter = 'left';
                range.label.verticalCenter = 'bottom';
                range.label.dy = -5;
                range.label.dx = 200;
            };

            const rangesByAxis = [];
            for (let i = 0; i < chart.yAxes.length; i++) {
                const axis = chart.yAxes.getIndex(i);
                if (!axis) continue;
                const r = axis.axisRanges.create();
                r.value = 0; // (placeholder) ✅ CHANGED: lo dejamos pero lo inicializamos bien luego
                applyRangeStyle(r);
                r.label.text = 'Ref 0';
                r.grid.disabled = true;
                r.label.disabled = true;

                rangesByAxis[i] = r;
            }

            let selectedAxisIndex = 0;
            let targetYAxis = chart.yAxes.getIndex(selectedAxisIndex);
            if (!targetYAxis) continue;

            let yRefRange = rangesByAxis[selectedAxisIndex];
            if (!yRefRange) continue;
            yRefRange.grid.disabled = false;
            yRefRange.label.disabled = false;

            // ✅ Handle más obvio: "píldora" con ↕
            const handle = chart.plotContainer.createChild(am4core.Container);
            handle.width = 38;
            handle.height = 28;
            handle.dy = -18; // sube 20px (como top negativo)
            handle.isMeasured = false;
            handle.zIndex = 999;
            handle.draggable = true;
            handle.cursorOverStyle = am4core.MouseCursorStyle.pointer;
            handle.hoverable = true;
            handle.rotation = 90; // grados
            // Fondo
            const handleBg = handle.createChild(am4core.RoundedRectangle);
            handleBg.width = am4core.percent(100);
            handleBg.height = am4core.percent(100);
            handleBg.fill = am4core.color('#2586bc');
            handleBg.stroke = am4core.color('#0b0e0f');
            handleBg.strokeWidth = 1;
            // Texto ↕
            const handleTxt = handle.createChild(am4core.Label);
            handleTxt.text = "<\u00A0\u00A0\u00A0>";
            handleTxt.fill = am4core.color('#ffffff');
            handleTxt.fontSize = 14;
            handleTxt.horizontalCenter = 'middle';
            handleTxt.verticalCenter = 'middle';
            handleTxt.align = 'center';
            handleTxt.valign = 'middle';
            handleTxt.x = am4core.percent(50);
            handleTxt.y = am4core.percent(50);

            const axisBtn = chart.plotContainer.createChild(am4core.Label);
            axisBtn.isMeasured = false;
            axisBtn.zIndex = 1000;
            axisBtn.cursorOverStyle = am4core.MouseCursorStyle.pointer;
            axisBtn.padding(2, 6, 2, 6);
            axisBtn.background.fill = am4core.color('#111111');
            axisBtn.background.fillOpacity = 0.85;
            axisBtn.fill = am4core.color('#ffffff');
            axisBtn.fontSize = 12;

            const updateAxisBtnText = () => {
                axisBtn.text =
                    chart.yAxes.length > 1
                        ? `Click aqui para cambiar de eje ${selectedAxisIndex + 1}/${chart.yAxes.length}`
                        : `Eje`;
            };

            let currentPixelY = null;

            const positionHandleAtPixelY = (py) => {
                handle.x = chart.plotContainer.pixelWidth / 2; // ✅ centro en X
                handle.y = py;
                axisBtn.x = 300;
                axisBtn.y = py - 20;
            };

            const setRefFromPixelY = (py) => {
                currentPixelY = py;
                const valueY = targetYAxis.yToValue(py);
                yRefRange.value = valueY;
                const formatted = targetYAxis?.numberFormatter
                    ? targetYAxis.numberFormatter.format(valueY)
                    : `${Number(valueY).toFixed(2)}`;

                yRefRange.label.text = formatted;
                positionHandleAtPixelY(py);
            };

            // ==========================================================
            // ✅✅✅ FIX DESFASE ZOOM (NUEVO, MINIMO)
            // ==========================================================
            const syncHandleToRangeValue = () => {
                if (!chart || chart.isDisposed()) return;
                if (!targetYAxis || !yRefRange) return;
                const value = yRefRange.value;
                const pos = targetYAxis.valueToPosition(value);
                const axisPoint = targetYAxis.renderer.positionToPoint(pos);
                const plotPoint = am4core.utils.spritePointToSprite(
                    axisPoint,
                    targetYAxis.renderer,
                    chart.plotContainer
                );
                const py = Math.max(0, Math.min(chart.plotContainer.pixelHeight, plotPoint.y));
                currentPixelY = py;
                positionHandleAtPixelY(py);
            };

            let yAxisExtremesDisposer = null;
            let yAxisSelectionDisposer = null;
            const bindYAxisSyncEvents = () => {
                try { yAxisExtremesDisposer?.dispose?.(); } catch (e) { }
                try { yAxisSelectionDisposer?.dispose?.(); } catch (e) { }
                yAxisExtremesDisposer = targetYAxis.events.on('extremeschanged', () => {
                    syncHandleToRangeValue();
                    initRefToMiddleOfAxis(); // ✅ NEW: por si aún no inicializó
                });
                disposers.push(yAxisExtremesDisposer);
                yAxisSelectionDisposer = targetYAxis.events.on('selectionextremeschanged', () => {
                    syncHandleToRangeValue();
                    initRefToMiddleOfAxis(); // ✅ NEW: por si aún no inicializó
                });
                disposers.push(yAxisSelectionDisposer);
            };

            // ✅ NEW: inicializar ref en el medio del eje cuando ya existan extremos/tamaño
            let didInitRef = false; // ✅ NEW
            const initRefToMiddleOfAxis = () => { // ✅ NEW
                if (didInitRef) return;
                if (!chart || chart.isDisposed()) return;
                if (!targetYAxis || !yRefRange) return;
                if (!chart.plotContainer || chart.plotContainer.pixelHeight <= 0) return;
                const min = targetYAxis.minZoomed ?? targetYAxis.min;
                const max = targetYAxis.maxZoomed ?? targetYAxis.max;
                if (min == null || max == null || !isFinite(min) || !isFinite(max) || min === max) return;
                const midValue = (min + max) / 2;
                yRefRange.value = midValue;      // ✅ centro del eje Y
                syncHandleToRangeValue();        // ✅ ubica handle sobre la línea
                // actualizar label con el formatter del eje
                const formatted = targetYAxis?.numberFormatter
                    ? targetYAxis.numberFormatter.format(midValue)
                    : `${Number(midValue).toFixed(2)}`;
                yRefRange.label.text = formatted;
                didInitRef = true;
            };

            bindYAxisSyncEvents();

            // ✅ CHANGED: en vez de usar pixelHeight/2 (a veces es 0), inicializamos por extremos del eje
            const readyInitDisposer = chart.events.on('ready', initRefToMiddleOfAxis); // ✅ NEW
            disposers.push(readyInitDisposer);
            const validatedInitDisposer = chart.events.on('datavalidated', initRefToMiddleOfAxis); // ✅ NEW
            disposers.push(validatedInitDisposer);
            initRefToMiddleOfAxis(); // ✅ NEW (intento inmediato)

            const sizeDisposer = chart.events.on('sizechanged', () => {
                if (!chart || chart.isDisposed()) return;
                // Si aún no inicializó, intentamos hacerlo ahora (cuando ya hay tamaño)
                initRefToMiddleOfAxis(); // ✅ NEW
                // Si ya había una posición guardada, mantenemos tu lógica
                if (currentPixelY != null) {
                    const py = Math.max(0, Math.min(chart.plotContainer.pixelHeight, currentPixelY));
                    setRefFromPixelY(py);
                }
                // extra: si el eje recalculó, re-sincroniza por value
                syncHandleToRangeValue();
            });
            disposers.push(sizeDisposer);

            const originalCursorBehavior = chart.cursor?.behavior;
            const originalCursorInteractionsEnabled = chart.cursor?.interactionsEnabled;

            let isDraggingRefLine = false;

            const downDisposer = handle.events.on('down', (ev) => {
                stopNativeEvent(ev);
                isDraggingRefLine = true;
                if (chart.cursor) {
                    chart.cursor.behavior = 'none';
                    chart.cursor.interactionsEnabled = false;
                    if (chart.cursor.selection) chart.cursor.selection.hide(0);
                }
            });
            disposers.push(downDisposer);

            const dragStartDisposer = handle.events.on('dragstart', (ev) => {
                stopNativeEvent(ev);
                isDraggingRefLine = true;
                if (chart.cursor) {
                    chart.cursor.behavior = 'none';
                    chart.cursor.interactionsEnabled = false;
                    if (chart.cursor.selection) chart.cursor.selection.hide(0);
                }
            });
            disposers.push(dragStartDisposer);

            const dragDisposer = handle.events.on('dragged', (ev) => {
                stopNativeEvent(ev);
                didInitRef = true; // ✅ NEW: ya lo movió el user, no re-inicializar
                const py = Math.max(0, Math.min(chart.plotContainer.pixelHeight, handle.pixelY));
                setRefFromPixelY(py);
            });
            disposers.push(dragDisposer);

            const dragStopDisposer = handle.events.on('dragstop', (ev) => {
                stopNativeEvent(ev);
                if (chart.cursor) {
                    chart.cursor.behavior = 'none';
                    chart.cursor.interactionsEnabled = false;
                    if (chart.cursor.selection) chart.cursor.selection.hide(0);
                }
                restoreCursorNextFrame(chart, originalCursorBehavior, originalCursorInteractionsEnabled);
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        isDraggingRefLine = false;
                    });
                });
            });
            disposers.push(dragStopDisposer);

            const upHandleDisposer = handle.events.on('up', (ev) => {
                stopNativeEvent(ev);
                if (chart.cursor) {
                    chart.cursor.behavior = 'none';
                    chart.cursor.interactionsEnabled = false;
                    if (chart.cursor.selection) chart.cursor.selection.hide(0);
                }
                restoreCursorNextFrame(chart, originalCursorBehavior, originalCursorInteractionsEnabled);
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        isDraggingRefLine = false;
                    });
                });
            });
            disposers.push(upHandleDisposer);

            const switchToAxis = (newAxisIndex) => {
                if (!chart || chart.isDisposed()) return;
                const newAxis = chart.yAxes.getIndex(newAxisIndex);
                const newRange = rangesByAxis[newAxisIndex];
                if (!newAxis || !newRange) return;
                if (yRefRange && !yRefRange.isDisposed()) {
                    yRefRange.grid.disabled = true;
                    yRefRange.label.disabled = true;
                }
                selectedAxisIndex = newAxisIndex;
                targetYAxis = newAxis;
                yRefRange = newRange;
                yRefRange.grid.disabled = false;
                yRefRange.label.disabled = false;

                updateAxisBtnText();

                // ✅ re-enganchar listeners al NUEVO eje
                bindYAxisSyncEvents();

                // ✅ CHANGED (opcional): al cambiar de eje, arrancar en el medio de ese eje
                didInitRef = false;          // ✅ NEW
                initRefToMiddleOfAxis();     // ✅ NEW

                // Si preferís mantener el mismo pixelY en vez de “medio del eje”, comentá las 2 líneas de arriba
                // y descomentá esto:
                // const py = Math.max(0, Math.min(chart.plotContainer.pixelHeight, currentPixelY ?? (chart.plotContainer.pixelHeight/2)));
                // setRefFromPixelY(py);
                // syncHandleToRangeValue();
            };

            updateAxisBtnText();

            const axisBtnDownDisposer = axisBtn.events.on('down', (ev) => {
                stopNativeEvent(ev);
            });
            disposers.push(axisBtnDownDisposer);

            const axisBtnUpDisposer = axisBtn.events.on('up', (ev) => {
                stopNativeEvent(ev);
                if (!chart || chart.isDisposed()) return;
                const nextIndex = (selectedAxisIndex + 1) % chart.yAxes.length;
                switchToAxis(nextIndex);
            });
            disposers.push(axisBtnUpDisposer);

            const upDisposer = interaction.events.on('up', (event) => {
                if (!chart || chart.isDisposed()) return;
                if (!chart.plotContainer || chart.plotContainer.isDisposed()) return;
                if (!chart.plotContainer.isHover) return;
                if (isDraggingRefLine) return;
                if (!event?.pointer?.point) return;
                didInitRef = true; // ✅ NEW
                const point = am4core.utils.documentPointToSprite(
                    event.pointer.point,
                    chart.plotContainer
                );
                const py = Math.max(0, Math.min(chart.plotContainer.pixelHeight, point.y));
                setRefFromPixelY(py);
            });
            disposers.push(upDisposer);

            const zoomEndedDisposer = chart.events.on('zoomended', () => {
                syncHandleToRangeValue();
            });
            disposers.push(zoomEndedDisposer);

            const startEndDisposer = dateAxis.events.on('startendchanged', () => {
                syncHandleToRangeValue();
            });
            disposers.push(startEndDisposer);

            // ==========================================================
            // ✅✅✅ CHECKBOX + ocultado “mentiroso”
            // ==========================================================
            const setRefLineVisible = (visible) => {
                handle.disabled = !visible;
                axisBtn.disabled = !visible;
                for (let i = 0; i < rangesByAxis.length; i++) {
                    const r = rangesByAxis[i];
                    if (!r || r.isDisposed()) continue;
                    r.grid.disabled = true;
                    r.label.disabled = true;
                }

                if (!visible) return;

                const active = rangesByAxis[selectedAxisIndex];
                if (active && !active.isDisposed()) {
                    active.grid.disabled = false;
                    active.label.disabled = false;
                }

                // ✅ si se muestra y aún no init, lo intentamos
                initRefToMiddleOfAxis(); // ✅ NEW

                if (currentPixelY != null) {
                    const py = Math.max(0, Math.min(chart.plotContainer.pixelHeight, currentPixelY));
                    setRefFromPixelY(py);
                } else {
                    // fallback si no hay pixel guardado
                    syncHandleToRangeValue();
                }
            };

            const toggleWrap = document.createElement('div');
            if (zoomBar) {
                toggleWrap.style.display = 'none';
            } else {
                toggleWrap.style.display = 'flex';
            }

            toggleWrap.style.gap = '8px';
            toggleWrap.style.margin = '8px 0';
            toggleWrap.style.marginLeft = '60px';

            const toggleInput = document.createElement('input');
            toggleInput.type = 'checkbox';
            toggleInput.checked = false;

            const toggleLabel = document.createElement('label');
            toggleLabel.textContent = 'Mostrar línea de referencia horizontal';
            toggleLabel.style.display = 'pointer';

            toggleLabel.style.cursor = 'none';
            toggleLabel.onclick = () => {
                toggleInput.checked = !toggleInput.checked;
                toggleInput.dispatchEvent(new Event('change'));
            };

            const setFakeHidden = (hidden) => {
                const op = hidden ? 0 : 1;
                yRefRange.grid.strokeOpacity = op;
                yRefRange.label.fillOpacity = op;
                yRefRange.label.background.fillOpacity = hidden ? 0 : 0.9;
                handle.opacity = op;
                axisBtn.opacity = op;
                handle.interactionsEnabled = !hidden;
                axisBtn.interactionsEnabled = !hidden;
            };

            // arranca "oculto" pero manteniendo todo creado/posicionado
            setRefLineVisible(true);
            setFakeHidden(true);

            const onToggleChange = () => {
                setFakeHidden(false);
                setRefLineVisible(toggleInput.checked);
                if (toggleInput.checked) {
                    syncHandleToRangeValue();
                }
            };

            toggleInput.addEventListener('change', onToggleChange);
            toggleWrap.appendChild(toggleInput);
            toggleWrap.appendChild(toggleLabel);
            chartsContainer.insertBefore(toggleWrap, chartDiv);

            disposers.push({
                dispose: () => {
                    try { toggleInput.removeEventListener('change', onToggleChange); } catch (e) { }
                    try { toggleWrap.remove(); } catch (e) { }
                },
            });
        }

        function gapCalc(granularity) {
            let res = 20;
            if (granularity) res = Number(granularity) * 4;
            return res;
        }

        function updateChart(chart, dataset, indexDataset) {
            let alias = '';
            switch (dataset.variable) {
                case 'TENSION_POR_FASE':
                    for (let i = 0; i < dataset.medidores[0]?.count_phases; i++) {
                        let color = dataset.color;
                        const phaseName = i === 0 ? 'R' : i === 1 ? 'S' : 'T';
                        const colorOffset = i === 0 ? '' : i === 1 ? '60' : '90';
                        alias = `Fase ${phaseName} (${dataset.alias})`;
                        addSeriesAndAxis(
                            chart,
                            dataset.data?.[`V_${phaseName}`],
                            '#.##V',
                            gapCalc(dataset.granularity),
                            color + colorOffset,
                            alias,
                            'Tensión por fase',
                            indexDataset
                        );
                    }
                    break;

                case 'CORRIENTE_POR_FASE':
                    for (let i = 0; i < dataset.medidores[0]?.count_phases; i++) {
                        let color = dataset.color;
                        const phaseName = i === 0 ? 'R' : i === 1 ? 'S' : 'T';
                        const colorOffset = i === 0 ? '' : i === 1 ? '60' : '90';
                        let alias = `Fase ${phaseName} (${dataset.alias})`;
                        addSeriesAndAxis(
                            chart,
                            dataset?.data?.[`I_${phaseName}`],
                            `#.##'A`,
                            gapCalc(dataset.granularity),
                            color + colorOffset,
                            alias,
                            'Corriente por fase',
                            indexDataset
                        );
                    }
                    break;

                case 'POTENCIA_ACTIVA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        dataset?.data?.P,
                        `#.##aW`,
                        gapCalc(dataset.granularity),
                        dataset.color,
                        `${dataset.alias} 
                              ${'Potencia activa total'}`,
                        'Potencia activa total',
                        indexDataset
                    );
                    break;

                case 'POTENCIA_ACTIVA_POR_FASE':
                    let nameLabelTooltipsP = '';
                    let colorOffsetP = '';
                    if (dataset.medidores[0]) {
                        dataset.medidores[0].count_phases += 1;
                    }
                    for (let i = 0; i < dataset.medidores[0]?.count_phases; i++) {
                        let color = dataset.color;
                        let phaseName = '';
                        if (i === 0) {
                            phaseName = 'P_R';
                            nameLabelTooltipsP = 'Fase R';
                            colorOffsetP = '60';
                        }
                        if (i === 1) {
                            phaseName = 'P_S';
                            nameLabelTooltipsP = 'Fase S';
                            colorOffsetP = '65';
                        }
                        if (i === 2) {
                            phaseName = 'P_T';
                            nameLabelTooltipsP = 'Fase T';
                            colorOffsetP = '70';
                        }
                        if (i === 3) {
                            phaseName = 'P';
                            nameLabelTooltipsP = 'Total';
                            colorOffsetP = '90';
                        }
                        alias = `${nameLabelTooltipsP} (${dataset.alias})
                              Potencia activa`;
                        addSeriesAndAxis(
                            chart,
                            dataset.data?.[`${phaseName}`],
                            `#.##aW`,
                            gapCalc(dataset.granularity),
                            color + colorOffsetP,
                            alias,
                            'Potencia activa por fase',
                            indexDataset
                        );
                    }
                    break;

                case 'POTENCIA_REACTIVA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        dataset.data?.Q,
                        `#.##aVAr`,
                        gapCalc(dataset.granularity),
                        dataset.color,
                        `${dataset.alias} 
                              ${'Potencia reactiva total'}`,
                        'Potencia reactiva total',
                        indexDataset
                    );
                    break;

                case 'POTENCIA_REACTIVA_POR_FASE':
                    let nameLabelTooltipsQ = '';
                    let colorOffsetQ = '';
                    if (dataset.medidores[0]) {
                        dataset.medidores[0].count_phases += 1;
                    }
                    for (let i = 0; i < dataset.medidores[0]?.count_phases; i++) {
                        let phaseName = '';
                        let color = dataset.color;
                        if (i === 0) {
                            phaseName = 'Q_R';
                            nameLabelTooltipsQ = 'Fase R';
                            colorOffsetQ = '60';
                        }
                        if (i === 1) {
                            phaseName = 'Q_S';
                            nameLabelTooltipsQ = 'Fase S';
                            colorOffsetQ = '65';
                        }
                        if (i === 2) {
                            phaseName = 'Q_T';
                            nameLabelTooltipsQ = 'Fase T';
                            colorOffsetQ = '70';
                        }
                        if (i === 3) {
                            phaseName = 'Q';
                            nameLabelTooltipsQ = 'Total';
                            colorOffsetQ = '90';
                        }
                        alias = `${nameLabelTooltipsQ} (${dataset.alias})
                              Potencia reactiva`;
                        addSeriesAndAxis(
                            chart,
                            dataset.data?.[`${phaseName}`],
                            `#.##aVAr`,
                            gapCalc(dataset.granularity),
                            color + colorOffsetQ,
                            alias,
                            'Potencia reactiva por fase',
                            indexDataset
                        );
                    }
                    break;

                case 'POTENCIA_ADQUIRIDA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        dataset?.data?.KW.map((item) => ({ ...item, valor: item.valor * 1000 })),
                        '#.##aW',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Potencia adquirida total'}`,
                        'Potencia adquirida total',
                        indexDataset
                    );
                    break;

                case 'ENERGIA_TOTAL_POR_HORA':
                    addSeriesAndAxis(
                        chart,
                        dataset?.data?.KWH.map((item) => ({ ...item, valor: item.valor * 1000 })),
                        '#.##aWh',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Energia total por hora'}`,
                        'Energia total por hora',
                        indexDataset
                    );
                    break;

                case 'ENERGIA_ACTIVA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        dataset?.data?.KWH.map((item) => ({ ...item, valor: item.valor * 1000 })),
                        '#.##aWh',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Energia activa total'}`,
                        'Energia activa total',
                        indexDataset
                    );
                    break;

                case 'TEMPERATURA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        dataset?.data?.TEMP,
                        `#.## °C`,
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Temperatura'}`,
                        'Temperatura',
                        indexDataset
                    );
                    break;

                case 'PRESION_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        dataset?.data?.PRESSURE,
                        `# hPa`,
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Presión'}`,
                        'Presión',
                        indexDataset
                    );
                    break;

                case 'HUMEDAD_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        dataset?.data?.HUMIDITY,
                        `#.## '%`,
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Humedad Total'}`,
                        'Humedad Total',
                        indexDataset
                    );
                    break;

                case 'ENERGIA_ACTIVA_IMPORTADA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        formattingSeries(dataset?.data?.KWH_I, dataset.variable, 'INSTANTANEOS'),
                        '#.##aWh',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Energía activa importada Total'}`,
                        'Energía activa importada Total',
                        indexDataset
                    );
                    break;

                case 'ENERGIA_ACTIVA_EXPORTADA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        formattingSeries(dataset?.data?.KWH_E, dataset.variable, 'INSTANTANEOS'),
                        '#.##aWh',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Energía activa exportada total'}`,
                        'Energía activa exportada total',
                        indexDataset
                    );
                    break;

                case 'ENERGIA_ACTIVA_GENERADA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        formattingSeries(dataset?.data?.KWH_G, dataset.variable, 'INSTANTANEOS'),
                        '#.##aWh',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Energía activa generada total'}`,
                        'Energía activa generada total',
                        indexDataset
                    );
                    break;

                case 'ENERGIA_ACTIVA_CONSUMIDA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        formattingSeries(dataset?.data?.KWH_C, dataset.variable, 'INSTANTANEOS'),
                        '#.##aWh',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Energía activa consumida total'}`,
                        'Energía activa consumida total',
                        indexDataset
                    );
                    break;

                case 'ENERGIA_ACTIVA_AUTOCONSUMIDA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        formattingSeries(dataset?.data?.KWH_AC, dataset.variable, 'INSTANTANEOS'),
                        '#.##aWh',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Energía activa autoconsumida total'}`,
                        'Energía activa autoconsumida total',
                        indexDataset
                    );
                    break;

                case 'POTENCIA_ACTIVA_GENERADA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        formattingSeries(dataset?.data?.P_G, dataset.variable, 'INSTANTANEOS'),
                        '#.##aW',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Potencia activa generada total'}`,
                        'Potencia activa generada total',
                        indexDataset
                    );
                    break;

                case 'POTENCIA_ACTIVA_CONSUMIDA_TOTAL':
                    addSeriesAndAxis(
                        chart,
                        formattingSeries(dataset?.data?.P_C, dataset.variable, 'INSTANTANEOS'),
                        '#.##aW',
                        180,
                        dataset.color,
                        `${dataset.alias} 
                              ${'Potencia activa consumida total'}`,
                        'Potencia activa consumida total',
                        indexDataset
                    );
                    break;
                default:
                    for (const key in dataset?.data) {
                        addSeriesAndAxis(
                            chart,
                            dataset?.data?.[key],
                            `#.##' ${dataset?.base_unit}'`, // ✅ CAMBIO: solo 2 decimales
                            10000,
                            dataset.color,
                            `${dataset.alias} ${key}`, // opcional: lo dejo en una línea
                            dataset?.base_unit,
                            indexDataset
                        );
                    }
                    break;
            }
        }

        function addSeriesAndAxis(chart, data, format, gap = 20, color, alias, title, indexDataset) {
            if (!data) return;

            const existingAxis = chart.yAxes.values.find(
                (axis) => axis.numberFormatter && axis.numberFormatter.numberFormat === format
            );

            const series = new am4charts.LineSeries();
            const numberFormatter = new am4core.NumberFormatter();
            numberFormatter.numberFormat = format;
            series.numberFormatter = numberFormatter;
            series.connect = false;

            if (existingAxis) {
                series.yAxis = existingAxis;
            }
            else {
                const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
                valueAxis.renderer.line.strokeOpacity = 1;
                valueAxis.renderer.line.strokeWidth = 2;
                valueAxis.title.text = title;
                valueAxis.numberFormatter = numberFormatter;
                if (indexDataset > 0) valueAxis.renderer.grid.template.disabled = true;
                series.yAxis = valueAxis;
            }

            formatsUsed.push(format);
            series.autoGapCount = gap;
            series.dataFields.valueY = 'valor';
            series.dataFields.dateX = 'timestamp';
            series.data = data;
            series.name = alias;
            series.stroke = am4core.color(color);
            series.tooltipText = `{valueY} - ${alias}`;
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color(color);
            const circleBullet = series.bullets.push(new am4charts.CircleBullet());
            circleBullet.circle.fill = am4core.color('#fff');
            series.minBulletDistance = 15;
            chart.series.push(series);
            chart.scrollbarX.series.push(series);
        }

        return () => {
            disposers.forEach((d) => {
                try { d.dispose(); } catch (e) { }
            });
            createdCharts.forEach((c) => {
                if (!c.isDisposed()) c.dispose();
            });
        };
        // eslint-disable-next-line
    }, []);

    return (
        <div
            id={`instantaneos-avanzado${ky ? `${ky}` : ''}`}
            style={{ height: '100%' }}
        />
    );
}